import { FC } from 'react';
import { ProductConfigurationInputs } from './ProductConfigurationModal';
import { CmsProduct, SeasonSelectionOption, ShopContent } from '../../../../types/cms_shared_types';
import { UserDateInput } from '../../../Common/UserDateInput';
import { Conditional } from '../../../Common/Conditional';
import { DayCount } from '../../../Common/DayCount';
import { UserBooleanInput } from '../../../Common/UserBooleanInput';
import { FormikErrors, useFormikContext } from 'formik';

interface SeasonSelectionProps {
  cmsShopContent: ShopContent;
  values: ProductConfigurationInputs;
  cmsProduct: CmsProduct;
  errors: FormikErrors<ProductConfigurationInputs>;
  fieldPrefix?: string;
}

export const SeasonSelection: FC<SeasonSelectionProps> = ({
  cmsShopContent,
  values,
  cmsProduct,
  errors,
  fieldPrefix,
}) => {
  const { setFieldValue } = useFormikContext();
  const fieldBase = fieldPrefix ? `${fieldPrefix}.` : '';

  const startDatePrompt = cmsProduct.startDatePrompt || undefined;
  const endDatePrompt = cmsProduct.endDatePrompt || undefined;
  const seasonsEnabled =
    cmsProduct.seasonSelection !== SeasonSelectionOption.DISABLED &&
    startDatePrompt !== undefined &&
    endDatePrompt !== undefined;

  const displaySeasonSelectionToggle = seasonsEnabled && cmsProduct.seasonSelection === SeasonSelectionOption.OPTIONAL;

  if (!seasonsEnabled) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 pt-4 sm:pt-0">
      <Conditional condition={displaySeasonSelectionToggle}>
        <UserBooleanInput
          name={`${fieldBase}isSeasonallyOpen`}
          title={cmsShopContent.productChoiceStep.productConfigurationModal.seasonallyOpen}
          description={cmsShopContent.productChoiceStep.productConfigurationModal.seasonallyOpenDescription}
          callback={(isSeasonallyOpen) => {
            if (isSeasonallyOpen) {
              setFieldValue(`${fieldBase}_timeMeasureInputSelection`, values.timeMeasureInput);
            } else {
              setFieldValue(`${fieldBase}timeMeasureInput`, values._timeMeasureInputSelection);
            }
          }}
        />
      </Conditional>
      <Conditional condition={values.isSeasonallyOpen}>
        <div>
          <div className="flex flex-row gap-3 mb-2">
            <UserDateInput
              name={`${fieldBase}startDate`}
              prompt={startDatePrompt}
              inputFormat="DD/MM"
              allowPastDates
              dateLimitLate={values.endDate}
            />
            <UserDateInput
              name={`${fieldBase}endDate`}
              prompt={endDatePrompt}
              inputFormat="DD/MM"
              allowPastDates
              dateLimitEarly={values.startDate}
            />
          </div>
          {values.startDate && values.endDate && values._timeMeasureInputSelection && (
            <DayCount
              formikParams={{ key: `${fieldBase}timeMeasureInput` }}
              textTemplate={cmsShopContent.productChoiceStep.productConfigurationModal.dayCountText}
              startDate={values.startDate}
              endDate={values.endDate}
              daysPerYear={values._timeMeasureInputSelection}
            />
          )}
          {(errors.startDate || errors.endDate) && (
            <div className="flex flex-col pl-4 mt-2 text-sm font-medium text-sami-red">
              {errors.startDate || errors.endDate}
            </div>
          )}
        </div>
      </Conditional>
    </div>
  );
};
